import * as React from "react";
import * as PropertiesSelector from "../../../../../../properties-selector";
import * as SharedState from "../../../../../../shared-state";
import * as OperationTimeGen2 from "../../../../../../operation-time-manager";
import * as CaseOverride from "../../../../../components/settings-overrides";
import styled from "styled-components";
import { PropertyDefinitions, SettingsOverrides } from "../../../../../tools";
import { PropertyValueSet } from "@genesys/property";
import { Dispatch } from "@typescript-tea/core";
import { Header } from "./header";
import { Body } from "./body";
import { Footer } from "./footer";
import { LoadType, LoadDef, WarningType } from "./types";
import { getWarningsAndErrors } from "./warning-and-error-handler";
import { BorderRadius } from "@genesys/ui-elements";

const Container = styled.div<{
  readonly warningType: WarningType;
}>`
  box-shadow: 0 5px 5px 0 rgb(0 0 0 / 10%);
  border: ${props => getBorderColor(props.warningType)};
  width: 399px;
  border-radius: ${BorderRadius.compact};
  background-color: white;
`;

export function View({
  isLoadNameEmpty,
  isLoadNameDuplicated,
  sharedState,
  propertiesSelectorState,
  caseOverrides,
  load,
  allPropertyDefinitions,
  isDisabled,
  titleJSX,
  operationTimeState,
  addTypeToLoad,
  removeTypeFromLoad,
  onOperationalTimeDispatch,
  caseOverridesDispatch,
  propertiesSelectorDispatch
}: {
  readonly isLoadNameEmpty: boolean;
  readonly isLoadNameDuplicated: boolean;
  readonly sharedState: SharedState.State;
  readonly load: LoadDef;
  readonly isDisabled: boolean;
  readonly allPropertyDefinitions: PropertyDefinitions;
  readonly titleJSX: JSX.Element;
  readonly caseOverrides: SettingsOverrides;
  readonly propertiesSelectorState: PropertiesSelector.State;
  readonly operationTimeState: OperationTimeGen2.State;
  readonly removeTypeFromLoad: (loadType: LoadType) => void;
  readonly addTypeToLoad: (loadType: LoadType) => void;
  readonly caseOverridesDispatch: Dispatch<CaseOverride.Action>;
  readonly onOperationalTimeDispatch: Dispatch<OperationTimeGen2.Action>;
  readonly propertiesSelectorDispatch: Dispatch<PropertiesSelector.Action>;
}) {
  const currentSelections = PropertiesSelector.getSelectedProperties(
    propertiesSelectorState
  )!;

  const manualInputKey = load.properties.manualInputKey;
  const manualInputEnabled = !!(
    manualInputKey &&
    PropertyValueSet.getInteger(manualInputKey, currentSelections)
  );

  const { messages, warningType } = getWarningsAndErrors(
    load,
    isLoadNameEmpty,
    isLoadNameDuplicated,
    propertiesSelectorState,
    allPropertyDefinitions,
    sharedState
  );

  return (
    <Container warningType={warningType}>
      <Header
        titleJsx={titleJSX}
        addTypeToLoad={addTypeToLoad}
        removeTypeFromLoad={removeTypeFromLoad}
        currentSelections={currentSelections}
        isDisabled={isDisabled}
        load={load}
        messages={messages}
        warningType={warningType}
      />
      <Body
        allPropertyDefinitions={allPropertyDefinitions}
        caseOverrides={caseOverrides}
        isDisabled={isDisabled}
        load={load}
        manualInputEnabled={manualInputEnabled}
        propertiesSelectorState={propertiesSelectorState}
        sharedState={sharedState}
        caseOverridesDispatch={caseOverridesDispatch}
        propertiesSelectorDispatch={propertiesSelectorDispatch}
      />
      <Footer
        onOperationalTimeDispatch={onOperationalTimeDispatch}
        removeTypeFromLoad={removeTypeFromLoad}
        operationTimeState={operationTimeState}
        selectedProperties={currentSelections}
        isDisabled={isDisabled}
        load={load}
        sharedState={sharedState}
      />
    </Container>
  );
}

function getBorderColor(warningType: WarningType) {
  if (warningType === "error") {
    return "2px solid red";
  } else if (warningType === "warning") {
    return "1px solid #F0C530";
  }

  return "1px solid #f1f5fa";
}
